import React, { useState, useCallback, useEffect } from 'react';
import { Form, Input, Select, Checkbox, Button, Upload } from 'antd';
import _ from 'lodash';
import { surveyQuestionTypes } from '@iso/constants/surveyQuestionTypes';
import { UploadOutlined } from '@ant-design/icons';
import { apiEndpoint } from '@iso/lib/helpers/endpoint';
import { authHeader } from '@iso/lib/helpers/authHeader';
import { visitorTypes } from '@iso/constants/visitorTypes';

const { Option } = Select;

// props: 
// name: name of the question
// questionType: type of the question
// isCheckinForm: is checkin form
// visitorTypes: visitor types
const QuestionFields = (props) => {
  const form = Form.useFormInstance();
  const name = props.name;
  const isCheckinForm = props.isCheckinForm || form.getFieldValue(['form', 'is_checkin_form']);
  const visitorTypesProp = props.visitorTypes || form.getFieldValue(['form', 'meta_data', 'visitor_types']);
  const selectedVisitorTypes = visitorTypesProp 
    ? visitorTypes.filter(
      (type) => visitorTypesProp.includes(type.value)
    )
    : [];

  const [questionType, setQuestionType] = useState(null);
  const [imageRemoved, setImageRemoved] = useState(false);

  useEffect(() => {
    setQuestionType(props.questionType || form.getFieldValue(['form', 'survey_question', name, 'question_type']));
  }, []);

  useEffect(() => {
    setQuestionType(props.questionType);
  }, [props.questionType]);

  // Reset imageRemoved state when component mounts or props change (modal reopens)
  useEffect(() => {
    const currentImageId = form.getFieldValue([name, 'meta_data', 'image_id']);
    const currentImageData = form.getFieldValue([name, 'meta_data', 'image']);
    
    // If form still has image data, reset the removed state (user didn't save the removal)
    if (currentImageId && currentImageData) {
      setImageRemoved(false);
    }
  }, [form, name, props]);
  
  const renderOptionsSurveyQuestion = () => {
    const options = [];
    _.forEach(surveyQuestionTypes, (type, index) => {
      options.push(
        <Option key={index} value={type.value}>
          {type.label}
        </Option>
      );
    });
    
    return (
      <Select
        placeholder="Select a question type"
        allowClear
        size="large"
        onChange={(value) => setQuestionType(value)}
      >
        {options}
      </Select>
    );
  };

  const renderRuleYesNo = useCallback(() => {
    return (
      <Select
        showSearch
        placeholder="Select"
        allowClear        
        filterOption={(input, option) =>
          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
        options={[
          { value: 'either', label: 'Either answer is valid' },
          { value: 'no', label: 'Visitor must answer "No"' },
          { value: 'yes', label: 'Visitor must answer "Yes"' },
        ]}
      />
    )
  }, []);

  const uploadImgProps = {
    name: "Image[file]",
    action: apiEndpoint.images,
    headers: authHeader(true),
    beforeUpload: (file, fileList) => {
      //
    },
    onChange: (response,) => {
      //
    },
    listType: "picture",
    multiple: false,
  };

  const renderQuestionFields = (questionType, name) => {
    switch (questionType) {
      case 'yes/no':
        return (
         <>
          <Form.Item
            name={[name, 'meta_data', 'rule']}
            rules={[{ required: true, message: 'This field is required' }]}
          >
            {renderRuleYesNo()}
          </Form.Item>
         </>
        );
      case 'multi_selection':
      case 'multiple_choice':
        return (
          <Form.Item
            name={[name, 'meta_data', 'options']}
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Select
              mode="tags"
              style={{
                width: '100%',
              }}
              placeholder="Enter options"
            />
          </Form.Item>
        );
      case 'fixed_text':
        return (
          <Form.Item
            name={[name, 'meta_data', 'text']}
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Input.TextArea autoSize rows={4} placeholder="Enter text" />           
          </Form.Item>
        )
      case 'fixed_image':
        const imageId = form.getFieldValue([name, 'meta_data', 'image_id']);
        const imageData = form.getFieldValue([name, 'meta_data', 'image']);
        const hasImage = (imageId && imageData && !imageRemoved);
        
        // Create file list for existing image
        const defaultFileList = hasImage ? [{
          uid: imageData.id.toString(),
          name: imageData.path.split('/').pop(),
          status: 'done',
          url: imageData.full_path,
          response: {
            image: imageData
          }
        }] : [];

        return (
          <Form.Item
            label="Image"
            name={[name, 'meta_data', "image_id"]}
            initialValue={imageId}
            getValueFromEvent={(info) => {
              // Return appropriate value for form field
              if (info.file.status === 'done' && info.file.response?.image?.id) {
                setImageRemoved(false); // Reset when new image uploaded
                form.setFieldValue([name, 'meta_data', 'image'], info.file.response.image);
                form.setFieldValue([name, 'meta_data', 'image_id'], info.file.response.image.id);
                return info.file.response.image.id;
              } else if (info.fileList.length === 0) {
                return undefined;
              } else if (hasImage && info.fileList.length > 0 && !info.file.response?.image?.id) {
                return imageId;
              }
              return form.getFieldValue([name, 'meta_data', 'image_id']);
            }}
            rules={[
              {
                required: true,
                message: "This field is required",
              },
            ]}
          >
            <Upload 
              {...uploadImgProps}
              defaultFileList={defaultFileList}
              maxCount={1}
              onRemove={() => {
                setImageRemoved(true); // Trigger re-render
                form.setFieldValue([name, 'meta_data', 'image_id'], undefined);
                form.setFieldValue([name, 'meta_data', 'image'], undefined);
                return true;
              }}
              beforeUpload={() => {
                // Prevent upload if there's already an image
                if (hasImage) {
                  return false;
                }
                return true;
              }}
              showUploadList={{
                showRemoveIcon: true,
                showPreviewIcon: true,
              }}
            >
              {!hasImage && (
                <Button
                  className="action-button"
                  icon={<UploadOutlined />}
                >
                  Upload
                </Button>
              )}
            </Upload>
          </Form.Item>
        );
      case 'upload_file':
      case 'signature':
      case 'number':
      case 'text':
      default:
        return <></>;
    }
  };

  const renderResponseConditionsFields = (fieldName) => {
    return (
      <>
        <Form.Item
          name={[fieldName, 'meta_data', 'response_required']}
          valuePropName="checked"
        >
          <Checkbox>Response Required</Checkbox>
        </Form.Item>

        <Form.Item
          name={[fieldName, 'meta_data', 'prefill_response']}
          valuePropName="checked"
        >
          <Checkbox>Prefill Response</Checkbox>
        </Form.Item>
      </>
    );
  };

  const renderVisibilityOptions = (fieldName) => {
    return (
      <Select
        placeholder="Select Visibility"
        allowClear
        mode="multiple"
        size="large"
      >
        {selectedVisitorTypes.map((type, index) => (
          <Option key={index} value={type.value}>
            <div>{type.label}</div>
          </Option>
        ))}
      </Select>
    );
  };

  return (
    <>
      <Form.Item
        name={[name, 'title']}
        label="Question Title"
        rules={[{ required: true, message: 'This field is required' }]}
      >
        <Input.TextArea placeholder="Enter question title" rows={2} />
      </Form.Item>
      
      <Form.Item
        name={[name, 'question_type']}
        label="Question Type"
        rules={[{ required: true, message: 'This field is required' }]}
      >
        {renderOptionsSurveyQuestion()}
      </Form.Item>

      {questionType && renderQuestionFields(questionType, name)}
      
      {renderResponseConditionsFields(name)}

      {isCheckinForm && <Form.Item
        name={[name, 'meta_data', 'question_visibility']}
        label="Visibility"
        rules={[{ 
          required: true, 
          message: 'This field is required' 
        }]}
      >
        {renderVisibilityOptions(name)}
      </Form.Item>}
    </>
  );
};

export default QuestionFields;